import React, { useState, useEffect } from 'react';
// import { useDispatch } from 'react-redux';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Typography,
  FormControl,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
//import { useNavigate } from 'react-router-dom';
import { AccountCircle } from '@mui/icons-material';
import { useNavigate, useMatch, matchPath, useLocation } from 'react-router-dom';
import NotificationBell from './NotificationBell';
import { useMQTT } from '../../Context/MQTTContext';
import i18n from '../../i18n';
import { useTranslation } from 'react-i18next';
// import { logout } from '../store/actions/authActions'

interface NavBarProps {
  onDrawerToggle: () => void;
}

const layoutRoutes = [
  '/layout/manage-choice-group',
  '/layout/manageItemCategory',
  '/layout/manageItemSubCategory',
  '/layout/manage-choice',
  '/layout/item-management',
  '/layout/orderManagement',
  "/layout/stockManagement",
  "/layout/storeManagement",
  '/layout/no-store',
  '/layout/dashboard',
  '/layout/cancellation-payment',
  '/layout/commissionTracking',
  '/layout/commissionTrackingHistory',
  '/layout/makeyourRestaurant',
  '/layout/settlements',
  '/layout/profile',
  '/layout/settings',
  '/layout/change-password',
  '/layout/orderManagement/view',
];

const NavBar: React.FC<NavBarProps> = ({ onDrawerToggle }) => {
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [language, setLanguage] = useState<string>(localStorage.getItem('hm_language') || 'en');
  const navigate = useNavigate();
  //   const dispatch = useDispatch();
  const { disconnect } = useMQTT();
  const { t } = useTranslation();

  // Sync language state with i18n
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      setLanguage(lng);
    };

    // Listen for language changes
    i18n.on('languageChanged', handleLanguageChange);

    // Cleanup listener on unmount
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  // Cross-tab logout effect
  React.useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === 'logout') {
        // Perform logout actions
        localStorage.setItem('userData', JSON.stringify(null));
        localStorage.setItem('userid', JSON.stringify(null));
        localStorage.setItem('userId', JSON.stringify(null));
        localStorage.setItem('token', JSON.stringify(null));
        localStorage.setItem('itemSubCategoryId', JSON.stringify(null));
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userId');
        localStorage.removeItem('userid');
        localStorage.removeItem('token');
        localStorage.removeItem('itemSubCategoryId');
        navigate("/", { replace: true });
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [navigate]);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const userDataString = localStorage.getItem("userData");

  let firstName = '';
  let lastName = '';

  if (userDataString) {
    try {
      const userData = JSON.parse(userDataString);
      firstName = userData?.userDetails?.firstName || '';
      lastName = userData?.userDetails?.lastName || '';
    } catch (error) {
      console.error("Failed to parse user data from localStorage:", error);
    }
  }

  const handleLanguageChange = (event: SelectChangeEvent) => {
    const value = event.target.value as string;
    setLanguage(value);
    localStorage.setItem('hm_language', value);
    // Update i18next language
    i18n.changeLanguage(value).then(() => {
      console.log('Language changed to:', value);
      console.log('Current i18n language:', i18n.language);
    }).catch((error) => {
      console.error('Error changing language:', error);
    });
  };

  const handleLogout = () => {
    disconnect(); // Use context disconnect
    localStorage.setItem('userData', JSON.stringify(null));
    localStorage.setItem('userid', JSON.stringify(null));
    localStorage.setItem('userId', JSON.stringify(null));
    localStorage.setItem('token', JSON.stringify(null));
    localStorage.setItem('itemSubCategoryId', JSON.stringify(null));
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('userId');
    localStorage.removeItem('userid');
    localStorage.removeItem('token');
    localStorage.removeItem('itemSubCategoryId');

    // Trigger cross-tab logout
    localStorage.setItem('logout', Date.now().toString());
    navigate("/",{replace:true})

  };


  const isNoStorePage = useMatch('/layout/no-store');

  // Check if the path is /layout/orderManagement/view/:id
  const matchOrderView = matchPath(
    { path: '/layout/orderManagement/view/:id', end: true },
    location.pathname
  );

  // Check if current path is one of the known static paths
  const isStaticKnownRoute = layoutRoutes.includes(location.pathname);
  const orderId = matchOrderView?.params?.id;

  const isKnownRoute = isStaticKnownRoute || Boolean(orderId);

  const shouldShowMenuIcon = isKnownRoute && !isNoStorePage;

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: 'white',
        color: '#333',
        boxShadow: 'none',
        borderBottom: '1px solid #eee',
      }}
    >
      <Toolbar>
        {shouldShowMenuIcon && (
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={onDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <img
            src="/logo.png"
            alt="ShopUp Food"
            style={{ height: '40px', marginRight: '10px', objectFit: 'contain',
                  filter: 'brightness(0) invert(0)' }}
          />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
            {t('common.brandName')}
          </Typography>
        </Box>

        {/* Right side items */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Language selector */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={language}
              onChange={handleLanguageChange}
              variant="outlined"
              sx={{
                height: '35px',
                backgroundColor: '#f5f5f5',
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              }}
            >
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="ar">العربية</MenuItem>
            </Select>
          </FormControl>

          {/* Notifications */}
          {/* <IconButton color="inherit">
            <Badge badgeContent={670} color="error" max={999}>
              <NotificationsIcon />
            </Badge>
          </IconButton> */}
          <NotificationBell />

          {/* User Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={handleMenu}
              sx={{
                borderRadius: 1,
                backgroundColor: '#f5f5f5',
                padding: '8px',
                '&:hover': {
                  backgroundColor: '#e0e0e0',
                },
              }}
            >
              <Typography variant="body2" sx={{ mr: 1, color: '#666' }}>
                {`${firstName} ${lastName}`}
              </Typography>
              <AccountCircle />
              {/* <AccountCircle />  */}
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              {/* <MenuItem  onClick={() => { handleClose(); navigate('/profile'); }}>My Profile</MenuItem>
              <MenuItem onClick={() => { handleClose(); navigate('/settings'); }}>Change Password</MenuItem> */}
              <MenuItem
                onClick={() => {
                  handleClose();
                  navigate('/layout/profile');
                }}
              >
                {t('nav.profile')}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleClose();
                  navigate('/layout/settings');
                }}
              >
                {t('nav.settings')}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleClose();
                  navigate('/layout/change-password');
                }}
              >
                {t('nav.changePassword')}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleClose();
                  handleLogout();
                }}
              >
                {t('nav.logout')}
              </MenuItem>              
            </Menu>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default NavBar;
